/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable import/prefer-default-export */

import { isElectron } from 'react-device-detect';

import logger from '@/logger';
import { GenericApiResponse } from '@/services/api';

export const setSessionItem = <T>(key: string, value: T): void => {
  try {
    const serializedValue = JSON.stringify(value);
    sessionStorage.setItem(key, serializedValue);
  } catch (error) {
    logger.info(`Error setting item in sessionStorage for key "${key}":`, error);
    throw error;
  }
};

export const getSessionItem = <T>(key: string): T | null => {
  try {
    const serializedValue = sessionStorage.getItem(key);
    if (serializedValue === null) {
      // eslint-disable-next-line unicorn/no-null
      return null;
    }
    return JSON.parse(serializedValue) as T;
  } catch (error) {
    logger.info(`Error getting item from sessionStorage for key "${key}":`, error);
    throw error;
  }
};

export const removeSessionItem = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    logger.info(`Error removing item from sessionStorage for key "${key}":`, error);
    throw error;
  }
};

export const COIN_BALANCE_SESSION_KEY = 'coinBalance';

const DEFAULT_COIN_BALANCE = 0;

/** Read coin balance from session storage. Use this everywhere instead of local state. */
export function getCoinBalance(): number {
  const value = getSessionItem<number>(COIN_BALANCE_SESSION_KEY);
  return typeof value === 'number' ? value : DEFAULT_COIN_BALANCE;
}

export const getLocalItem = <T>(key: string): T | null => {
  try {
    const serializedValue = localStorage.getItem(key);
    if (serializedValue === null) {
      // eslint-disable-next-line unicorn/no-null
      return null;
    }
    return JSON.parse(serializedValue) as T;
  } catch (error) {
    logger.info(`Error getting item from localStorage for key "${key}":`, error);
    throw error;
  }
};

export const setLocalItem = <T>(key: string, value: T): void => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (error) {
    logger.info(`Error setting item in localStorage for key "${key}":`, error);
    throw error;
  }
};

const getCommonParameters = () => {
  return {
    adminId: localStorage.getItem('adminId'),
    studentId: getSessionItem('studentId'),
    sessionId: getSessionItem('sessionId'),
  };
};

export const getAdminCommonHeaders = () => {
  const sessionId = getSessionItem('adminSessionId');
  const adminId = localStorage.getItem('adminId');
  const headers: Record<string, string> = {};
  if (sessionId && typeof sessionId === 'string') {
    headers['session-id'] = sessionId;
  }
  if (adminId) {
    headers['admin-id'] = adminId;
  }
  return headers;
};

export const createKey = (
  base: string,
  parameters: Record<string, string | undefined | null | number> = {},
) => {
  const commonParameters = getCommonParameters();
  const allParameters = { ...parameters, ...commonParameters };
  const filteredParameters = Object.fromEntries(
    Object.entries(allParameters)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== undefined && value !== null)
      .map(([key, value]) => [key, value?.toString() || '']),
  );
  const queryString = new URLSearchParams(filteredParameters).toString();
  return [`${base}?${queryString}`];
};

export const getIconName = (name: string) => {
  const nameParts = name.trim().split(' ') || [];
  const firstNameInitial = nameParts[0]?.[0] ?? '';
  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const lastNameInitial = nameParts.at(-1)?.[0] ?? '';
  const iconName = `${firstNameInitial}${lastNameInitial}`.toUpperCase();
  return iconName;
};

export const getStoryVideoUrl = (appLanguage: string, isFln: boolean) =>
  `https://fusion-static-media.swiftpal.ai/converted-to-hls/${
    isFln ? 'fln-' : ''
  }story-videos/${appLanguage}/master.m3u8`;

export const getPasswordLabelKey = (item: string) =>
  `studentLogin:password${item.charAt(0).toUpperCase() + item.slice(1)}`;

export function isApiResponseSuccess<T>(data: GenericApiResponse<T>) {
  return Math.floor(data.status / 100) === 2;
}

export const areObjectsNotEqual = (object1: unknown, object2: unknown) => {
  if (object1 === object2) return false;
  if (
    typeof object1 !== 'object' ||
    typeof object2 !== 'object' ||
    object1 === null ||
    object2 === null
  ) {
    return true;
  }

  const objectA = object1 as Record<string, unknown>;
  const objectB = object2 as Record<string, unknown>;

  const stack: Array<{ obj1: Record<string, unknown>; obj2: Record<string, unknown> }> = [
    { obj1: objectA, obj2: objectB },
  ];

  while (stack.length > 0) {
    const currentPair = stack.pop()!;
    const currentObject1 = currentPair.obj1;
    const currentObject2 = currentPair.obj2;

    const keys1 = Object.keys(currentObject1);
    const keys2 = Object.keys(currentObject2);

    if (keys1.length !== keys2.length) {
      return true;
    }

    for (const key of keys1) {
      if (!Object.prototype.hasOwnProperty.call(currentObject2, key)) {
        return true;
      }

      const value1 = currentObject1[key];
      const value2 = currentObject2[key];

      if (value1 === value2) {
        // eslint-disable-next-line no-continue
        continue;
      }
      if (
        typeof value1 === 'object' &&
        typeof value2 === 'object' &&
        value1 !== null &&
        value2 !== null
      ) {
        stack.push({
          obj1: value1 as Record<string, unknown>,
          obj2: value2 as Record<string, unknown>,
        });
      } else {
        return true;
      }
    }
  }

  return false;
};

export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const newIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[newIndex]] = [shuffled[newIndex], shuffled[index]];
  }
  const isShuffled = !shuffled.every((item, index) => {
    return !areObjectsNotEqual(item, array[index]);
  });

  if (!isShuffled) {
    [shuffled[0], shuffled[1]] = [shuffled[1], shuffled[0]];
  }
  return shuffled;
}

export function arraysAreEqual(array1: string[], array2: string[]): boolean {
  if (array1.length !== array2.length) return false;
  for (const [index, element] of array1.entries()) {
    if (element !== array2[index]) return false;
  }
  return true;
}
export const onStopPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
  event.stopPropagation();
};

export const isDesktop = () => {
  return isElectron;
};

function getElectronPlatform(): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }
  const value: unknown = window.electronEnv;
  if (value === undefined || typeof value !== 'object' || value === null) {
    return undefined;
  }
  const { platform } = value as { platform?: unknown };
  return typeof platform === 'string' ? platform : undefined;
}

/** Node `os` cannot run in the Vite/browser bundle; platform comes from `electron/preload`. */
export const isLinux = () => getElectronPlatform() === 'linux';

export const getYoutubeEmbedLink = (link: string | undefined) => {
  const regExpVideoId = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|\?v=)([^#&?]*).*/;
  const groups = link?.match(regExpVideoId);
  // eslint-disable-next-line unicorn/no-null
  const videoId = groups && groups.length === 3 ? groups[2] : null;
  return `https://www.youtube.com/embed/${videoId}`;
};

/* eslint-disable no-bitwise, unicorn/prefer-math-trunc, unicorn/prefer-code-point, no-plusplus */
export const getHashCode = (input: string): number => {
  let hash = 0;
  const { length } = input;

  for (let index = 0; index < length; index++) {
    hash = (hash << 5) - hash + input.charCodeAt(index);
    hash |= 0; // Force to 32-bit int
  }

  return Math.abs(hash);
};

export function getTriggerArrayForCoins(coins: number): (1 | 2 | 3)[] {
  if (coins <= 0) return [];
  const threes = Math.floor(coins / 3);
  const remainder = coins % 3;
  const array: (1 | 2 | 3)[] = [];
  for (let index = 0; index < threes; index += 1) array.push(3);
  if (remainder === 1) array.push(1);
  else if (remainder === 2) array.push(2);
  return array;
}

export const onOpenInWebview = (ssoUrl: string, name: string) => {
  if (!ssoUrl) return;
  if (isDesktop() && window.electronAPI?.openSsoWindow) {
    void window.electronAPI.openSsoWindow(ssoUrl, name);
  } else {
    window.open(ssoUrl, '_blank', 'noopener,noreferrer');
  }
};
/* eslint-enable no-bitwise, unicorn/prefer-math-trunc, unicorn/prefer-code-point, no-plusplus */
